import type { ActivationGroup } from '@/features/activationGroup/types'
import type { MarketProgram } from '@/features/bidding/constants'
import { BidType, MFRR_MARKET_PROGRAMS } from '@/features/bidding/constants'

/**
 * BID-279 transient solution (context: BID-242, proper fix: ML-1215).
 *
 * The combined-strategy autotrader (Amptank) bids these regulation objects on mFRR energy, but its
 * killswitch (`combined_strategy_killswitch`) lives in the trading Unleash instance, which
 * FlexPortal is not wired to. Until the whitelists are consolidated (ML-1215), the ROs are
 * hardcoded here and hidden from manual mFRR energy bidding unless
 * FLEX_PORTAL_AMPTANK_AUTOMATED_MFRR_BIDDING_KILLSWITCH is enabled in the general Unleash.
 * Same polarity as PM's PM_AUTOMATED_MFRR_BIDDING_KILLSWITCH: enabled = autotrader killed =
 * ROs shown for manual bidding; disabled or missing = bot alive = hidden.
 *
 * These ROs must NOT be added to portfolio-manager's `auto_traded_regulation_objects` env config
 * instead: that value also feeds EAM_AUTOTRADER_RO_BOOK on msa-dt-trade-scheduler-finland — the
 * mFRR autotrader's trading book — so listing them there would enrol them in that bot too.
 *
 * Operational contract: whoever flips `combined_strategy_killswitch` in the trading Unleash must
 * flip this killswitch in the general Unleash in tandem — bot killed -> enable this one too so
 * traders can bid the ROs manually; bot live -> keep it disabled.
 */
export const AMPTANK_AUTOMATED_MFRR_BIDDING_KILLSWITCH_TOGGLE = 'FLEX_PORTAL_AMPTANK_AUTOMATED_MFRR_BIDDING_KILLSWITCH'

// The same bundle serves every environment, so the list holds each environment's codes; a code
// that does not exist in the current environment never matches anything.
export const AUTO_TRADED_AMPTANK_REGULATION_OBJECT_CODES: string[] = [
  // Production: Amptank UBRECB (Kemijärvi BESS)
  'R18SYMPOWER',
  // Production: Amptank ATNO
  'R19SYMPOWER',
  // Staging: mimics UBRECB
  'FIUBRO01',
  // Staging: mimics ATNO
  'FIATRO01',
]

// Mirrors portfolio-manager's auto_traded_regulation_objects filtering in
// GET /activation-group/available: exact regulationObjectCode match, MFRR_UP/MFRR_DOWN + ENERGY only.
export function isAutoTradedAmptankActivationGroup(
  activationGroup: ActivationGroup,
  marketProgram: MarketProgram,
  bidType: BidType,
): boolean {
  return (
    bidType === BidType.ENERGY &&
    MFRR_MARKET_PROGRAMS.includes(marketProgram) &&
    activationGroup.regulationObjectCode != null &&
    AUTO_TRADED_AMPTANK_REGULATION_OBJECT_CODES.includes(activationGroup.regulationObjectCode)
  )
}
